import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../auth/useAuth';
import { shellService, type ShellChrome, type ShellNotification } from '../services';
import { cn } from '../ui/cn';
import { Icon } from '../ui/icons';
import { CACHE } from '../app/query-config';
import { GlobalSearch } from './search/GlobalSearch';
import { useDismiss } from './useDismiss';

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter((p) => p.length > 0);
  const initials = parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
  return initials.length > 0 ? initials : '?';
}

/**
 * `bg-warning/[.16]` — ARBITRARY FORM, and it has to be.
 *
 * This read `bg-warning/16`, which emitted NO CSS AT ALL: Tailwind's opacity
 * modifier only accepts values from its own scale (…/10, /20, /25…), and 16 is
 * not on it. An unrecognised modifier is not an error, it is silently dropped —
 * so warning-toned notification chips have been rendering with a transparent
 * background, while tsc, biome and every unit test stayed green, because none
 * of them can see whether a class name resolves to a rule.
 *
 * Verified against the compiled stylesheet rather than by eye: the built CSS
 * contains `bg-warning\/\[\.16\]` and never contained `bg-warning\/16`.
 */
const TONE_CHIP: Readonly<Record<ShellNotification['tone'], string>> = Object.freeze({
  danger: 'bg-brand/10 text-brand',
  warning: 'bg-warning/[.16] text-warning-ink',
  neutral: 'bg-neutral-200 text-neutral-700',
  success: 'bg-[#e9f2ec] text-[#2f6b47]',
});

export interface TopBarProps {
  onOpenDrawer: () => void;
  isMobile: boolean;
}

export function TopBar({ onOpenDrawer, isMobile }: TopBarProps): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * THE TRAY SUBSCRIBES TO THE CACHE ITSELF, rather than taking notifications
   * as props from `AppShell`.
   *
   * This started as props and it was wrong: "Mark all read" lives here and
   * updates the cache here, but a component that reads its state from a prop
   * cannot see its own write — it only re-renders when the PARENT happens to,
   * which makes "the badge clears immediately" an accident of where the query
   * was declared.
   *
   * Same query key as `AppShell`, so react-query dedupes it to one request.
   * Owning the subscription is what makes `setQueryData` below re-render this
   * component directly, with no reload and no refetch in between.
   */
  const { data: chrome } = useQuery({
    queryKey: ['shell', 'chrome'],
    queryFn: ({ signal }) => shellService.get(signal),
    ...CACHE.chrome,
  });

  const needsAttention: readonly ShellNotification[] = chrome?.needsAttention ?? [];
  const recentActivity: readonly ShellNotification[] = chrome?.recentActivity ?? [];
  const unreadCount = chrome?.unreadCount ?? 0;

  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const menuRef = useDismiss(menuOpen, () => setMenuOpen(false));
  const notifRef = useDismiss(notifOpen, () => setNotifOpen(false));

  const unread = unreadCount > 0;

  /**
   * "MARK ALL READ" — the button that did nothing.
   *
   * It had no `onClick` at all. Now it calls the server, and the UI updates
   * WITHOUT A RELOAD because the mutation writes the new chrome straight into
   * the query cache on success: the dot, the count and the per-row highlight
   * all read from that one cache entry, so they clear together in the same
   * render.
   *
   * `setQueryData` rather than only `invalidateQueries` is the difference
   * between "updates immediately" and "updates once the refetch lands". The
   * invalidate still runs afterwards so the list reconciles with the server, but
   * nothing on screen is waiting for it.
   */
  const markAllRead = useMutation({
    mutationFn: () => shellService.markAllRead(),
    onSuccess: () => {
      queryClient.setQueryData<ShellChrome>(['shell', 'chrome'], (current) =>
        current === undefined
          ? current
          : {
              ...current,
              unreadCount: 0,
              needsAttention: current.needsAttention.map((n) => ({ ...n, unread: false })),
              recentActivity: current.recentActivity.map((n) => ({ ...n, unread: false })),
            },
      );
      void queryClient.invalidateQueries({ queryKey: ['shell', 'chrome'] });
    },
  });

  return (
    <header className="flex min-h-topbar flex-none items-center gap-4 border-b border-neutral-300 bg-surface-raised px-[22px]">
      {isMobile && (
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open navigation"
          className="grid h-9 w-9 flex-none place-items-center rounded-[8px] text-neutral-700 hover:bg-neutral-100"
        >
          <Icon name="menu" size={20} />
        </button>
      )}

      {/*
        THE SEARCH BOX IS ALIVE.

        It was a `disabled` input with a placeholder advertising invoices —
        dead markup that looked like a feature, the same species as the "New"
        button removed below and the "Mark all read" button that had no
        `onClick`. It now talks to `GET /api/search`, and because `TopBar` is
        rendered by `AppShell` — the layout element wrapping every authenticated
        route — it is reachable from every screen in the app rather than from
        one page.

        Part D (2026-11-03) extended the endpoint from four entity types to
        nine — leads, customers, bookings, tours, invoices, payments, credit
        notes, vouchers and expenses — so the placeholder now names a shorter,
        representative set rather than all nine; see `GlobalSearch.tsx`.
      */}
      <GlobalSearch />

      <div className="ml-auto flex items-center gap-2.5">
        {/*
          The global "New" button is GONE, not disabled.

          It was a permanently-disabled affordance with a tooltip explaining
          that it did not work yet. Every module that can create a record now
          carries its own create action next to the records it creates ("New
          Lead" / "New Booking" on the dashboard, "New tour", "Record payment",
          and so on), so a global stub has nothing left to promise. A control
          that is always disabled teaches people to distrust the ones that are
          not.
        */}
        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={() => setNotifOpen((v) => !v)}
            aria-label={
              unread
                ? `Notifications, ${unreadCount} unread`
                : 'Notifications, none unread'
            }
            aria-expanded={notifOpen}
            className="relative grid h-[38px] w-[38px] place-items-center rounded-[8px] border border-neutral-300 bg-surface-raised text-neutral-700 hover:bg-neutral-100"
          >
            <Icon name="bell" size={17} />
            {unread && (
              <span className="absolute right-1.5 top-1.5 h-[7px] w-[7px] rounded-full bg-brand ring-2 ring-white" />
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-[46px] z-[60] w-notif-panel animate-gy-in-fast overflow-hidden rounded-[10px] border border-neutral-300 bg-surface-raised shadow-menu">
              <div className="flex items-center border-b border-neutral-200 px-[15px] py-[13px]">
                <span className="text-[13px] font-bold leading-none">
                  Notifications
                  {unread && (
                    <span className="ml-1.5 text-[11.5px] font-semibold text-brand">
                      {unreadCount}
                    </span>
                  )}
                </span>
                {/*
                  Disabled when there is nothing unread, so the button never
                  again looks like it did something it did not. Note the tray
                  stays OPEN afterwards — the rows visibly lose their highlight,
                  which is the feedback that the click landed.
                */}
                <button
                  type="button"
                  disabled={!unread || markAllRead.isPending}
                  onClick={() => {
                    markAllRead.mutate();
                  }}
                  className="ml-auto text-[11.5px] font-medium leading-none text-brand hover:underline disabled:cursor-not-allowed disabled:text-neutral-400 disabled:no-underline"
                >
                  {markAllRead.isPending ? 'Marking…' : 'Mark all read'}
                </button>
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                {/*
                  Empty state. The tray used to be backed by a four-item fixture
                  and could never be empty; against a real API — and against a
                  freshly seeded database — empty is the ordinary case, and an
                  unexplained blank panel reads as a failure.

                  ONLY shown when BOTH sections are empty — it used to say
                  "Nothing needs your attention" even when the activity list
                  below had real rows, which was simply wrong once a second
                  section existed.
                */}
                {needsAttention.length === 0 && recentActivity.length === 0 && (
                  <p className="px-[15px] py-6 text-center text-[12px] leading-[1.5] text-neutral-600">
                    Nothing yet.
                    <span className="mt-1 block text-[11px] text-neutral-500">
                      Departures, due balances and today's updates show up here.
                    </span>
                  </p>
                )}
                {markAllRead.isError && (
                  <p
                    role="alert"
                    className="border-b border-neutral-200 px-[15px] py-2.5 text-[11.5px] leading-[1.4] text-danger-ink"
                  >
                    Could not mark those read. Try again.
                  </p>
                )}
                {needsAttention.length > 0 && (
                  <div>
                    <div className="bg-[#f7f5f3] px-[15px] py-1.5 text-[10.5px] font-bold uppercase tracking-caps text-neutral-500">
                      Needs attention
                    </div>
                    {needsAttention.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => {
                          setNotifOpen(false);
                          if (n.to !== undefined) navigate(n.to);
                        }}
                        className={cn(
                          'flex w-full gap-[11px] border-b border-neutral-200 px-[15px] py-[13px] text-left last:border-b-0 hover:bg-[#f7f5f3]',
                          n.unread && 'bg-brand/[.03]',
                        )}
                      >
                        <span
                          className={cn(
                            'grid h-[30px] w-[30px] flex-none place-items-center rounded-[7px]',
                            TONE_CHIP[n.tone],
                          )}
                        >
                          <Icon name={n.icon} size={15} strokeWidth={2.2} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12.8px] font-semibold leading-[1.35]">{n.title}</span>
                          <span className="mt-[3px] block text-xs leading-[1.4] text-neutral-600">
                            {n.detail}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {recentActivity.length > 0 && (
                  <div>
                    <div className="bg-[#f7f5f3] px-[15px] py-1.5 text-[10.5px] font-bold uppercase tracking-caps text-neutral-500">
                      Recent activity
                    </div>
                    {recentActivity.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => {
                          setNotifOpen(false);
                          if (n.to !== undefined) navigate(n.to);
                        }}
                        className={cn(
                          'flex w-full gap-[11px] border-b border-neutral-200 px-[15px] py-[13px] text-left last:border-b-0 hover:bg-[#f7f5f3]',
                          n.unread && 'bg-brand/[.03]',
                        )}
                      >
                        <span
                          className={cn(
                            'grid h-[30px] w-[30px] flex-none place-items-center rounded-[7px]',
                            TONE_CHIP[n.tone],
                          )}
                        >
                          <Icon name={n.icon} size={15} strokeWidth={2.2} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12.8px] font-semibold leading-[1.35]">{n.title}</span>
                          <span className="mt-[3px] block text-xs leading-[1.4] text-neutral-600">
                            {n.detail}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setNotifOpen(false);
                  navigate('/notifications');
                }}
                className="block w-full border-t border-neutral-200 px-[15px] py-2.5 text-center text-[11.5px] font-semibold leading-none text-brand hover:bg-[#f7f5f3]"
              >
                View all
              </button>
            </div>
          )}
        </div>

        <div className="h-[26px] w-px bg-neutral-300" />

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            className="flex items-center gap-2.5 rounded-[24px] border border-neutral-300 bg-surface-raised py-[5px] pl-[5px] pr-[9px] hover:bg-neutral-100"
          >
            <span className="grid h-[29px] w-[29px] place-items-center rounded-full bg-brand-gradient-diag text-[11.5px] font-bold leading-none text-white">
              {initialsOf(user?.fullName ?? '')}
            </span>
            <span className="hidden text-left lg:block">
              <span className="block max-w-[140px] truncate text-[12.5px] font-semibold leading-[1.15]">
                {user?.fullName ?? 'Signed in'}
              </span>
              <span className="mt-0.5 block text-[10.5px] leading-[1.15] text-neutral-600">
                {user?.role.name ?? ''}
              </span>
            </span>
            <Icon name="chevron-down" size={14} className="text-neutral-600" />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-12 z-[60] w-user-menu animate-gy-in-fast overflow-hidden rounded-[10px] border border-neutral-300 bg-surface-raised shadow-menu"
            >
              <div className="border-b border-neutral-200 px-3.5 py-3">
                <div className="truncate text-[12.8px] font-semibold leading-none">
                  {user?.fullName}
                </div>
                <div className="mt-1 truncate text-[11.5px] leading-none text-neutral-600">
                  {user?.email}
                </div>
              </div>
              {/*
                The design's "View as role" switcher is deliberately not built.
                The server resolves permissions per request from the JWT's
                roleId, so a client control that changes your own role either
                does nothing real or is a genuine privilege hole. Impersonation,
                if wanted, belongs on the server behind an Owner-only audited
                endpoint.
              */}
              <div className="p-2">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                  }}
                  className="w-full rounded-[6px] px-2.5 py-2.5 text-left text-[12.5px] font-semibold leading-none text-danger-ink hover:bg-brand/[.07]"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
